/**
 * Noise Extraction scene — lit directional beams + bounce reflect (no SFX).
 */
import Phaser from "phaser";
import { noiseExtractionCatalog } from "../data/noise-extraction-catalog";
import {
  NeEvent,
  applyMoveTarget,
  createNoiseExtractionState,
  drainEvents,
  getHudSnapshot,
  getSolidBlocks,
  restartNoiseExtraction,
  tickNoiseExtraction,
  type NoiseExtractionState,
} from "../features/noise-extraction";
import {
  castLootRimPulse,
  castSonarPulse,
  objectInteriorColor,
  planObstacleOutlineStamp,
  playerSonarRailColor,
  pursuerSonarLifeMul,
  pursuerSonarRailColor,
  strokeAabbLocalOutline,
  strokeCircleLocalOutline,
  tipKissUsesObstacleOutline,
  SONAR_LOOT_RADIUS,
  type SonarBeam,
} from "../features/noise-extraction/view/sonar-fx";
import type { NePursuerMode } from "../features/noise-extraction";
import {
  SonarLineParticlePool,
  type LineParticle,
} from "../features/noise-extraction/view/line-particle-pool";
import {
  fillSolidBlockEraseMask,
  residualFadeAlpha,
} from "../features/noise-extraction/view/memory-paint-age";
import { drawThreatLockCue } from "../features/noise-extraction/view/threat-lock-fx";
import { stepDisplayPos } from "../features/portal-ffa/view/presentation-smooth";
import { shouldRestartOnQuit } from "../flow/entry";
import { SceneKey, assertTransition } from "../flow/game-flow";
import { sharedInput } from "../input/input-controller";
import { mountNoiseExtractionHud } from "../ui/huds/noise-extraction";
import { getUiLayout } from "../ui/layout";
import { clearOverlay } from "../ui/screen-overlay";
import { markActiveScene } from "../ui/screen-state";

const NeDepth = {
  ground: 0,
  blocksGhost: 1,
  /** Persistent explored memory (under live fog). */
  fogMemory: 2,
  fog: 3,
  echoes: 5,
  exit: 8,
  actors: 10,
  fx: 20,
  extract: 22,
} as const;

type BurstRing = {
  gfx: Phaser.GameObjects.Graphics;
  life: number;
  maxLife: number;
  radius: number;
  x: number;
  y: number;
  color: number;
};

export class NoiseExtractionScene extends Phaser.Scene {
  private state!: NoiseExtractionState;
  private hud!: ReturnType<typeof mountNoiseExtractionHud>;
  private disposed = false;
  private playerGfx!: Phaser.GameObjects.Arc;
  private sonarGfx!: Phaser.GameObjects.Graphics;
  /** Live black fog punched by current light. */
  private fogRt!: Phaser.GameObjects.RenderTexture;
  /** Persistent white mask of visited light paths (fog-of-war open). */
  private memoryRt!: Phaser.GameObjects.RenderTexture;
  /**
   * Persistent afterimage paint (aged blue + object interiors).
   * Pixel buffer — not per-segment GameObjects / Graphics command lists.
   * Ages toward planar void; solid terrain interiors are erased each frame.
   */
  private memoryPaintRt!: Phaser.GameObjects.RenderTexture;
  /**
   * Tip-kiss wall/loot/exit silhouettes — ages slower than residual rails so
   * terrain awareness stays readable when the player looks back.
   */
  private terrainPaintRt!: Phaser.GameObjects.RenderTexture;
  /** Scratch: fog-erase stamp (cleared every use). */
  private stampGfx!: Phaser.GameObjects.Graphics;
  /** Scratch: blue/object paint for this frame's delta only. */
  private paintGfx!: Phaser.GameObjects.Graphics;
  private liveStampGfx!: Phaser.GameObjects.Graphics;
  private beamGfx!: Phaser.GameObjects.Graphics;
  private exitGfx!: Phaser.GameObjects.Arc;
  private pursuerGfx: Phaser.GameObjects.Arc[] = [];
  private pursuerCue!: Phaser.GameObjects.Graphics;
  private blockGfx: Phaser.GameObjects.Rectangle[] = [];
  private bursts: BurstRing[] = [];
  private linePool!: SonarLineParticlePool;
  private pursuerLinePool!: SonarLineParticlePool;
  private pursuerBeamGfx!: Phaser.GameObjects.Graphics;
  private extractBar!: Phaser.GameObjects.Graphics;
  private displayX = 0;
  private displayY = 0;
  private sonarAcc = 0;
  private sonarClock = 0;
  private sonarCellKey = "";
  private sonarCellCoolUntil = 0;
  /** Tip-kiss obstacle outline bake cooldown (block/loot key → until sonarClock). */
  private outlineCoolUntil = new Map<string, number>();
  private wasMoving = false;
  private groundGfx!: Phaser.GameObjects.Graphics;

  constructor() {
    super(SceneKey.NoiseExtraction);
  }

  create(): void {
    this.disposed = false;
    markActiveScene(SceneKey.NoiseExtraction);
    sharedInput.attach();
    clearOverlay();
    getUiLayout().sync();
    this.cameras.main.setBackgroundColor("#05070c");

    this.state = createNoiseExtractionState();
    this.displayX = this.state.player.x;
    this.displayY = this.state.player.y;
    this.buildWorld();
    this.setupCamera();

    this.hud = mountNoiseExtractionHud({
      onRestart: () => this.restartRun(),
      onQuit: () => this.quitToTitle(),
    });
    this.hud.sync(getHudSnapshot(this.state));

    this.input.on("pointerdown", this.onPointerDown, this);
    this.scale.on("resize", this.onResize, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    this.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardown());
  }

  private setupCamera(): void {
    const map = noiseExtractionCatalog.balance.map;
    const lerp = noiseExtractionCatalog.balance.cameraFollowLerp;
    this.cameras.main.setBounds(
      map.xMin,
      map.yMin,
      map.xMax - map.xMin,
      map.yMax - map.yMin,
    );
    this.cameras.main.startFollow(this.playerGfx, true, lerp, lerp);
    this.cameras.main.setDeadzone(40, 40);
  }

  private buildWorld(): void {
    const b = noiseExtractionCatalog.balance;
    const map = b.map;
    const solids = getSolidBlocks();

    this.groundGfx?.destroy();
    this.groundGfx = this.add.graphics().setDepth(NeDepth.ground);
    this.groundGfx.fillStyle(0x0a1018, 1);
    this.groundGfx.fillRect(
      map.xMin,
      map.yMin,
      map.xMax - map.xMin,
      map.yMax - map.yMin,
    );
    this.groundGfx.lineStyle(1, 0x152033, 0.25);
    for (let x = map.xMin; x <= map.xMax; x += 80) {
      this.groundGfx.lineBetween(x, map.yMin, x, map.yMax);
    }
    for (let y = map.yMin; y <= map.yMax; y += 80) {
      this.groundGfx.lineBetween(map.xMin, y, map.xMax, y);
    }

    for (const rect of this.blockGfx) rect.destroy();
    this.blockGfx = [];
    for (const block of solids) {
      // Near-invisible ghosts — world is read from beam reflects only
      const r = this.add
        .rectangle(block.x, block.y, block.w, block.h, 0x121a28, 0.06)
        .setStrokeStyle(1, 0x2a3a52, 0.08)
        .setDepth(NeDepth.blocksGhost);
      this.blockGfx.push(r);
    }

    this.exitGfx?.destroy();
    this.exitGfx = this.add
      .circle(this.state.exit.x, this.state.exit.y, b.extractRadius, 0x1e4d3a, 0.04)
      .setStrokeStyle(1, 0x4ade80, 0.1)
      .setDepth(NeDepth.exit);

    this.fogRt?.destroy();
    this.memoryRt?.destroy();
    this.memoryPaintRt?.destroy();
    this.terrainPaintRt?.destroy();
    this.stampGfx?.destroy();
    this.paintGfx?.destroy();
    this.liveStampGfx?.destroy();
    this.sonarGfx?.destroy();
    this.beamGfx?.destroy();

    const mapW = map.xMax - map.xMin;
    const mapH = map.yMax - map.yMin;
    // Fog-open mask (hidden)
    this.memoryRt = this.add
      .renderTexture(map.xMin, map.yMin, mapW, mapH)
      .setOrigin(0, 0)
      .setDepth(NeDepth.fogMemory)
      .setVisible(false);
    this.memoryRt.clear();
    // Afterimage canvas — residual rails (ages toward planar void)
    this.memoryPaintRt = this.add
      .renderTexture(map.xMin, map.yMin, mapW, mapH)
      .setOrigin(0, 0)
      .setDepth(NeDepth.fogMemory);
    this.memoryPaintRt.clear();
    // Terrain/object silhouettes — above residual, slower fade
    this.terrainPaintRt = this.add
      .renderTexture(map.xMin, map.yMin, mapW, mapH)
      .setOrigin(0, 0)
      .setDepth(NeDepth.fogMemory);
    this.terrainPaintRt.clear();
    this.fogRt = this.add
      .renderTexture(map.xMin, map.yMin, mapW, mapH)
      .setOrigin(0, 0)
      .setDepth(NeDepth.fog);
    this.stampGfx = this.make.graphics({ x: 0, y: 0 }, false);
    this.paintGfx = this.make.graphics({ x: 0, y: 0 }, false);
    this.liveStampGfx = this.make.graphics({ x: 0, y: 0 }, false);
    this.sonarGfx = this.add.graphics().setDepth(NeDepth.echoes);
    this.beamGfx = this.add.graphics().setDepth(NeDepth.echoes + 1);
    this.pursuerBeamGfx?.destroy();
    this.pursuerBeamGfx = this.add.graphics().setDepth(NeDepth.echoes + 1);

    this.playerGfx?.destroy();
    for (const g of this.pursuerGfx) g.destroy();
    this.pursuerGfx = [];
    this.pursuerCue?.destroy();
    this.extractBar?.destroy();
    this.playerGfx = this.add
      .circle(this.state.player.x, this.state.player.y, 2, 0x000000, 0)
      .setDepth(NeDepth.actors)
      .setVisible(false);
    for (const p of this.state.pursuers) {
      this.pursuerGfx.push(
        this.add
          .circle(p.x, p.y, b.pursuerRadius, 0x334155, 0.35)
          .setStrokeStyle(1, 0x64748b, 0.35)
          .setDepth(NeDepth.actors),
      );
    }
    this.pursuerCue = this.add.graphics().setDepth(NeDepth.fx);
    this.extractBar = this.add.graphics().setDepth(NeDepth.extract);

    this.exitGfx.setAlpha(0);

    const bCap = noiseExtractionCatalog.balance;
    const tipMul = Math.max(0.1, bCap.sonarTipSpeedMul);
    if (
      !this.linePool ||
      this.linePool.beamCapacity !== bCap.sonarBeamPoolCap
    ) {
      this.linePool = new SonarLineParticlePool({
        beamCapacity: bCap.sonarBeamPoolCap,
        contactCapacity: bCap.sonarContactPoolCap,
        tipSpeedMul: tipMul,
      });
    } else {
      this.linePool.tipSpeedMul = tipMul;
      this.linePool.clear();
    }
    if (
      !this.pursuerLinePool ||
      this.pursuerLinePool.beamCapacity !== bCap.sonarBeamPoolCap
    ) {
      this.pursuerLinePool = new SonarLineParticlePool({
        beamCapacity: bCap.sonarBeamPoolCap,
        contactCapacity: bCap.sonarContactPoolCap,
        tipSpeedMul: tipMul,
      });
    } else {
      this.pursuerLinePool.tipSpeedMul = tipMul;
      this.pursuerLinePool.clear();
    }
    this.sonarAcc = 0;
    this.sonarClock = 0;
    this.sonarCellKey = "";
    this.sonarCellCoolUntil = 0;
    this.wasMoving = false;
  }

  private onResize = (): void => {
    getUiLayout().sync();
  };

  private onPointerDown = (pointer: Phaser.Input.Pointer): void => {
    if (this.disposed) return;
    if (this.state.phase === "escaped" || this.state.phase === "caught") {
      return;
    }
    applyMoveTarget(this.state, pointer.worldX, pointer.worldY);
  };

  update(_t: number, dtMs: number): void {
    if (this.disposed) return;
    const pressed = sharedInput.consumePressed();
    if (pressed.cancel) {
      this.quitToTitle();
      return;
    }
    const dt = Math.min(0.05, dtMs / 1000);
    tickNoiseExtraction(this.state, dt);
    const events = drainEvents(this.state);
    for (const ev of events) {
      if (ev.kind === NeEvent.NoiseBurst) {
        // Sibling lookfeel owns loot circles — skip big rings; hold uses PlayerSonar.
        if (ev.radius < 40) this.spawnBurst(ev.x, ev.y, ev.radius);
      } else if (ev.kind === NeEvent.Footstep && ev.emitSonar) {
        this.spawnLobeBurst(ev);
      } else if (ev.kind === NeEvent.PursuerSonar) {
        this.spawnPursuerLobeBurst(ev);
      } else if (ev.kind === NeEvent.PlayerSonar) {
        this.spawnPlayerSonar(ev);
      } else if (
        ev.kind === NeEvent.AlertChanged &&
        ev.from === 0 &&
        ev.to === 1 &&
        ev.cause === "sonar_hit"
      ) {
        this.spawnAlertHalo(this.state.player.x, this.state.player.y);
      }
    }
    this.syncView(dt);
    this.hud.sync(getHudSnapshot(this.state));
  }

  private spawnBurst(x: number, y: number, radius: number): void {
    const gfx = this.add.graphics().setDepth(NeDepth.fx);
    const maxLife = noiseExtractionCatalog.balance.pulseFadeSec;
    this.bursts.push({
      gfx,
      life: maxLife,
      maxLife,
      radius,
      x,
      y,
      color: 0xfde68a,
    });
  }

  /** One-shot player-center red ring — “my pulse caused this” (AlertChanged 0→1). */
  private spawnAlertHalo(x: number, y: number): void {
    const b = noiseExtractionCatalog.balance;
    const gfx = this.add.graphics().setDepth(NeDepth.fx);
    const maxLife = b.sonarAlertHaloSec;
    this.bursts.push({
      gfx,
      life: maxLife,
      maxLife,
      radius: b.sonarAlertHaloRadius,
      x,
      y,
      color: b.sonarLightRed,
    });
  }

  private pursuerCastTargets(): { x: number; y: number; radius: number }[] {
    const r = noiseExtractionCatalog.balance.pursuerRadius + 4;
    return this.state.pursuers.map((p) => ({ x: p.x, y: p.y, radius: r }));
  }

  private castArgs(ox: number, oy: number, facingX: number, facingY: number, sideAmp: number, forwardPower: number, scale: number, rangeMul: number, rayMul: number) {
    const b = noiseExtractionCatalog.balance;
    return castSonarPulse({
      ox,
      oy,
      facingX,
      facingY,
      sideAmp,
      forwardPower,
      scale,
      maxRange: b.sonarMaxRange * rangeMul,
      rayCount: Math.max(12, Math.floor(b.sonarRayCount * rayMul)),
      blocks: getSolidBlocks(),
      loot: this.state.loot,
      exit: {
        x: this.state.exit.x,
        y: this.state.exit.y,
        radius: b.extractRadius,
      },
      pursuers: this.pursuerCastTargets(),
    });
  }

  /** Shared wall-clock life for player + pursuer stride rails (lifeMul = 1). */
  private strideSonarLife(): number {
    return noiseExtractionCatalog.balance.echoFadeSec * 1.15;
  }

  private spawnLobeBurst(ev: {
    x: number;
    y: number;
    facingX: number;
    facingY: number;
    sideAmp: number;
    forwardPower: number;
    scale: number;
    footSide?: number;
  }): void {
    const b = noiseExtractionCatalog.balance;
    const fl = Math.hypot(ev.facingX, ev.facingY) || 1;
    const fx = ev.facingX / fl;
    const fy = ev.facingY / fl;
    // Perpendicular sway — alternating feet so origins drift while walking
    const side = ev.footSide ?? 0;
    const off = b.sonarFootOffsetPx * side;
    const ox = ev.x - fy * off;
    const oy = ev.y + fx * off;
    const beams = this.castArgs(
      ox,
      oy,
      ev.facingX,
      ev.facingY,
      ev.sideAmp,
      ev.forwardPower,
      ev.scale,
      b.sonarStrideRangeMul,
      b.sonarStrideRayMul,
    );
    // Live stride rail = light blue (not aged memory blue)
    this.linePool.emitBeams(
      beams,
      this.strideSonarLife(),
      "preferSpent",
      b.sonarLightBlue,
    );
  }

  private spawnPursuerLobeBurst(ev: {
    pursuerId: string;
    mode: NePursuerMode;
    pulse: "stride" | "heartbeat";
    x: number;
    y: number;
    facingX: number;
    facingY: number;
    sideAmp: number;
    forwardPower: number;
    scale: number;
    footSide: number;
  }): void {
    const b = noiseExtractionCatalog.balance;
    const isHeartbeat = ev.pulse === "heartbeat";
    let beams;
    if (isHeartbeat) {
      beams = castSonarPulse({
        ox: ev.x,
        oy: ev.y,
        facingX: ev.facingX,
        facingY: ev.facingY,
        sideAmp: ev.sideAmp,
        forwardPower: ev.forwardPower,
        scale: ev.scale,
        maxRange: b.heartbeatSonarMaxRange,
        rayCount: Math.max(8, Math.floor(b.heartbeatSonarRayCount)),
        blocks: getSolidBlocks(),
        loot: this.state.loot,
        exit: {
          x: this.state.exit.x,
          y: this.state.exit.y,
          radius: b.extractRadius,
        },
        pursuers: null,
        player: {
          x: this.state.player.x,
          y: this.state.player.y,
          radius: b.playerRadius + 4,
        },
      });
    } else {
      const fl = Math.hypot(ev.facingX, ev.facingY) || 1;
      const fx = ev.facingX / fl;
      const fy = ev.facingY / fl;
      // Identical stride keys as player — rail color from event mode snapshot.
      const off = b.sonarFootOffsetPx * ev.footSide;
      const ox = ev.x - fy * off;
      const oy = ev.y + fx * off;
      beams = castSonarPulse({
        ox,
        oy,
        facingX: ev.facingX,
        facingY: ev.facingY,
        sideAmp: ev.sideAmp,
        forwardPower: ev.forwardPower,
        scale: ev.scale,
        maxRange: b.sonarMaxRange * b.sonarStrideRangeMul,
        rayCount: Math.max(
          12,
          Math.floor(b.sonarRayCount * b.sonarStrideRayMul),
        ),
        blocks: getSolidBlocks(),
        loot: this.state.loot,
        exit: {
          x: this.state.exit.x,
          y: this.state.exit.y,
          radius: b.extractRadius,
        },
        pursuers: null,
        player: {
          x: this.state.player.x,
          y: this.state.player.y,
          radius: b.playerRadius + 4,
        },
      });
    }
    const rail = pursuerSonarRailColor(ev.mode, {
      sonarLightPursuer: b.sonarLightPursuer,
      sonarLightRed: b.sonarLightRed,
    });
    const lifeMul = isHeartbeat
      ? b.heartbeatSonarLifeMul
      : pursuerSonarLifeMul(ev.mode, b.sonarChaseLifeMul);
    const life = this.strideSonarLife() * Math.max(0.05, lifeMul);
    this.pursuerLinePool.emitBeams(beams, life, "preferSpent", rail);
  }

  /** Presentation only — alert hit SSOT already ran in rules via castPlayerSonarForAlert. */
  private spawnPlayerSonar(ev: {
    x: number;
    y: number;
    facingX: number;
    facingY: number;
    sideAmp: number;
    forwardPower: number;
    scale: number;
    footSide: number;
    tint: "player" | "orange" | "hold" | "loot_low" | "loot_high" | "heartbeat";
    lifeMul: number;
    maxRange: number;
    rayCount: number;
    rimEmit: { cx: number; cy: number; radius: number } | null;
  }): void {
    if (ev.scale <= 0.01) return;
    const b = noiseExtractionCatalog.balance;
    const lootTargets = ev.rimEmit
      ? this.state.loot.filter(
          (l) =>
            Math.hypot(l.x - ev.rimEmit!.cx, l.y - ev.rimEmit!.cy) > 0.5,
        )
      : this.state.loot;
    const shared = {
      scale: ev.scale,
      maxRange: ev.maxRange,
      rayCount: ev.rayCount,
      blocks: getSolidBlocks(),
      loot: lootTargets,
      exit: {
        x: this.state.exit.x,
        y: this.state.exit.y,
        radius: b.extractRadius,
      },
      pursuers: this.pursuerCastTargets(),
    };
    const beams = ev.rimEmit
      ? castLootRimPulse({
          cx: ev.rimEmit.cx,
          cy: ev.rimEmit.cy,
          radius: ev.rimEmit.radius,
          ...shared,
        })
      : (() => {
          const fl = Math.hypot(ev.facingX, ev.facingY) || 1;
          const fx = ev.facingX / fl;
          const fy = ev.facingY / fl;
          const off = b.sonarFootOffsetPx * (ev.footSide || 0);
          const ox = ev.x - fy * off;
          const oy = ev.y + fx * off;
          return castSonarPulse({
            ox,
            oy,
            facingX: ev.facingX,
            facingY: ev.facingY,
            sideAmp: ev.sideAmp,
            forwardPower: ev.forwardPower,
            ...shared,
          });
        })();
    const rail = playerSonarRailColor(ev.tint, {
      sonarOrange: b.sonarOrange,
      sonarLightBlue: b.sonarLightBlue,
    });
    const life = b.echoFadeSec * 1.15 * Math.max(0.05, ev.lifeMul);
    this.linePool.emitBeams(beams, life, "preferSpent", rail);
  }

  private ingestBeams(
    beams: SonarBeam[],
    opts?: { preserveFlight?: boolean },
  ): void {
    const life = noiseExtractionCatalog.balance.echoFadeSec * 1.15;
    const mode = opts?.preserveFlight ? "preferSpent" : "overwriteOldest";
    this.linePool.emitBeams(beams, life, mode);
  }

  private sonarCellKeyAt(x: number, y: number, fx: number, fy: number): string {
    const b = noiseExtractionCatalog.balance;
    const cell = Math.max(8, b.sonarCellSize);
    const bins = Math.max(4, Math.floor(b.sonarFacingBins));
    const ang = Math.atan2(fy, fx);
    const face = Math.floor(((ang + Math.PI) / (Math.PI * 2)) * bins) % bins;
    return `${Math.floor(x / cell)},${Math.floor(y / cell)},${face}`;
  }

  /** Moving: free pulse. Idle: no-op (idleSonarScale=0 + scene skips idle path). */
  private tryPulseSonar(): void {
    const b = noiseExtractionCatalog.balance;
    const snap = getHudSnapshot(this.state);
    if (snap.sonarScale <= 0.01) return;
    const moving = snap.player.moving;
    if (!moving) {
      const key = this.sonarCellKeyAt(
        this.displayX,
        this.displayY,
        snap.player.facingX,
        snap.player.facingY,
      );
      if (key === this.sonarCellKey && this.sonarClock < this.sonarCellCoolUntil) {
        return;
      }
      this.sonarCellKey = key;
      this.sonarCellCoolUntil = this.sonarClock + b.sonarSameCellCooldownSec;
    } else {
      // Moving — clear cell lock so idle cooldown doesn't linger
      this.sonarCellKey = "";
      this.sonarCellCoolUntil = 0;
    }
    this.pulseSonar();
  }

  private pulseSonar(): void {
    const snap = getHudSnapshot(this.state);
    if (snap.sonarScale <= 0.01) return;
    const b = noiseExtractionCatalog.balance;
    const beams = this.castArgs(
      this.displayX,
      this.displayY,
      snap.player.facingX,
      snap.player.facingY,
      snap.lobeSideAmp,
      snap.lobeForwardPower,
      snap.sonarScale,
      1,
      1,
    );
    const life = b.echoFadeSec * 1.15;
    this.linePool.emitBeams(beams, life, "preferSpent", b.sonarLightBlue);
  }

  /**
   * Flatten leftover residual rail chroma toward planar void, then erase solid
   * terrain interiors so the blend never paints walls. Terrain tip-kiss outlines
   * live on a separate slower-aging RT.
   */
  private ageMemoryPaint(dt: number): void {
    const b = noiseExtractionCatalog.balance;
    const map = b.map;
    const fadeColor = b.memoryPaintFadeColor;
    const residualA = residualFadeAlpha(dt, b.memoryPaintFadePerSec);
    if (residualA > 0.0005) {
      this.memoryPaintRt.fill(fadeColor, residualA);
    }
    const terrainA = residualFadeAlpha(dt, b.terrainPaintFadePerSec);
    if (terrainA > 0.0005) {
      this.terrainPaintRt.fill(fadeColor, terrainA);
    }
    // Terrain occlusion: residual must not fill solid blocks.
    fillSolidBlockEraseMask(this.stampGfx, getSolidBlocks());
    this.memoryPaintRt.erase(this.stampGfx, -map.xMin, -map.yMin);
    this.stampGfx.clear();
  }

  /** Persist pursuer/player interior threat wash into paint RT (loot/exit: rail only). */
  private stampObjectMemory(p: LineParticle): void {
    const interior = objectInteriorColor(p.kind);
    if (interior == null) return;
    const nl = Math.hypot(p.nx, p.ny) || 1;
    const nx = p.nx / nl;
    const ny = p.ny / nl;
    const r = 11 + p.amp * 5;
    const ix = p.hx - nx * (r * 0.45);
    const iy = p.hy - ny * (r * 0.45);
    const map = noiseExtractionCatalog.balance.map;
    this.stampGfx.clear();
    this.stampGfx.fillStyle(0xffffff, 1);
    this.stampGfx.fillCircle(ix, iy, r * 1.15);
    this.memoryRt.draw(this.stampGfx, -map.xMin, -map.yMin);
    this.paintGfx.clear();
    this.paintGfx.fillStyle(interior, 0.35);
    this.paintGfx.fillCircle(ix, iy, r * 1.35);
    this.paintGfx.fillStyle(interior, 0.55);
    this.paintGfx.fillCircle(ix, iy, r);
    this.memoryPaintRt.draw(this.paintGfx, -map.xMin, -map.yMin);
  }

  /**
   * Tip-kiss → local perimeter glow at contact into fog-open + terrain silhouette RTs
   * (not full obstacle outline; not live thick contact blobs).
   * Shared by player blue / pursuer red / scrape orange / loot-hold identity rails.
   */
  private stampHitObstacleOutline(p: LineParticle): void {
    const b = noiseExtractionCatalog.balance;
    const plan = planObstacleOutlineStamp({
      kind: p.kind,
      hx: p.hx,
      hy: p.hy,
      nx: p.nx,
      ny: p.ny,
      amp: p.amp,
      railColor: p.color,
      blocks: getSolidBlocks(),
      extractRadius: b.extractRadius,
      lootRadius: SONAR_LOOT_RADIUS,
    });
    if (plan == null) return;

    const until = this.outlineCoolUntil.get(plan.key) ?? 0;
    if (this.sonarClock < until) return;
    this.outlineCoolUntil.set(plan.key, this.sonarClock + 0.55);

    const map = b.map;
    this.stampGfx.clear();
    this.paintGfx.clear();
    if (plan.shape === "aabb") {
      strokeAabbLocalOutline(
        this.stampGfx,
        plan.block,
        plan.hx,
        plan.hy,
        0xffffff,
        1,
        plan.wSoft + 0.9,
        plan.halfLen,
      );
      strokeAabbLocalOutline(
        this.paintGfx,
        plan.block,
        plan.hx,
        plan.hy,
        plan.color,
        plan.aSoft,
        plan.wSoft,
        plan.halfLen,
      );
      strokeAabbLocalOutline(
        this.paintGfx,
        plan.block,
        plan.hx,
        plan.hy,
        plan.color,
        plan.aHard,
        plan.wHard,
        plan.halfLen * 0.55,
      );
    } else {
      strokeCircleLocalOutline(
        this.stampGfx,
        plan.cx,
        plan.cy,
        plan.radius,
        plan.hx,
        plan.hy,
        0xffffff,
        1,
        plan.wSoft + 0.9,
        plan.halfAngle,
      );
      strokeCircleLocalOutline(
        this.paintGfx,
        plan.cx,
        plan.cy,
        plan.radius,
        plan.hx,
        plan.hy,
        plan.color,
        plan.aSoft,
        plan.wSoft,
        plan.halfAngle,
      );
      strokeCircleLocalOutline(
        this.paintGfx,
        plan.cx,
        plan.cy,
        plan.radius,
        plan.hx,
        plan.hy,
        plan.color,
        plan.aHard,
        plan.wSoft * 0.55,
        plan.halfAngle * 0.55,
      );
    }
    this.memoryRt.draw(this.stampGfx, -map.xMin, -map.yMin);
    this.terrainPaintRt.draw(this.paintGfx, -map.xMin, -map.yMin);
  }

  /** Shared tip-kiss path — outline RT for wall/loot/exit; threat wash otherwise. */
  private onSonarTipKiss(
    pool: SonarLineParticlePool,
    p: LineParticle,
    peak: number,
    contactLifeFor: (p: LineParticle) => number,
  ): void {
    if (tipKissUsesObstacleOutline(p.kind)) {
      this.stampHitObstacleOutline(p);
      return;
    }
    pool.emitContact(p, peak, contactLifeFor(p));
    this.stampObjectMemory(p);
  }

  private syncView(dt: number): void {
    const b = noiseExtractionCatalog.balance;
    const snap = getHudSnapshot(this.state);
    const pos = stepDisplayPos(
      this.displayX,
      this.displayY,
      this.state.player.x,
      this.state.player.y,
      b.displayFollowHz,
      b.displaySnapPx,
      dt,
    );
    this.displayX = pos.x;
    this.displayY = pos.y;
    this.playerGfx.setPosition(this.displayX, this.displayY);

    const moving = snap.player.moving;
    this.sonarClock += dt;
    this.sonarAcc += dt;
    // Move sonar is footstep-stride primary; idle standing does not pulse
    // (idleSonarScale=0 + no timed idle path). Move-start kickoff kept.
    // Rare move backup only (balance.sonarMovePulsePeriodSec ~1s+).
    if (moving && !this.wasMoving) {
      // One kickoff from body so the first stride isn't blank
      this.sonarCellKey = "";
      this.sonarCellCoolUntil = 0;
      this.pulseSonar();
      this.sonarAcc = 0;
    }
    this.wasMoving = moving;
    if (moving && this.sonarAcc >= b.sonarMovePulsePeriodSec) {
      // Soft backup if stride stalls (wall slide / slow crawl)
      this.sonarAcc = 0;
      this.tryPulseSonar();
    }

    // Tip-kiss: wall/loot/exit → RT outline (no thick live stamp). Threat → contact pool.
    const contactLifeBase = b.revealStampSec * 0.95;
    const scrapeContactLife =
      contactLifeBase * Math.max(1, b.scrapeSonarLifeMul);
    const ageMul = Math.max(0.1, b.sonarAgeMul);
    const contactLifeFor = (p: LineParticle): number => {
      if (p.kind === "wall" && p.color === b.sonarOrange) {
        return scrapeContactLife;
      }
      return contactLifeBase;
    };
    const { sawExit, sawPursuer } = this.linePool.update(
      dt,
      ageMul,
      (p, peak) => this.onSonarTipKiss(this.linePool, p, peak, contactLifeFor),
    );
    this.pursuerLinePool.update(dt, ageMul, (p, peak) =>
      this.onSonarTipKiss(this.pursuerLinePool, p, peak, contactLifeFor),
    );

    // Progressive memory: stamp delta into textures (drawing, not line objects)
    this.stampGfx.clear();
    this.paintGfx.clear();
    this.linePool.stampMemoryProgressive(
      this.stampGfx,
      this.paintGfx,
      b.fogMemoryHalfWidth,
    );
    this.memoryRt.draw(this.stampGfx, -b.map.xMin, -b.map.yMin);
    this.memoryPaintRt.draw(this.paintGfx, -b.map.xMin, -b.map.yMin);
    this.stampGfx.clear();
    this.paintGfx.clear();
    this.pursuerLinePool.stampMemoryProgressive(
      this.stampGfx,
      this.paintGfx,
      b.fogMemoryHalfWidth,
    );
    this.memoryRt.draw(this.stampGfx, -b.map.xMin, -b.map.yMin);
    this.memoryPaintRt.draw(this.paintGfx, -b.map.xMin, -b.map.yMin);

    // Leftover wave chroma → planar void; terrain silhouettes age slower.
    // Solids erase residual fill so walls are never painted over by the blend.
    this.ageMemoryPaint(dt);

    // Fog: unexplored black; remembered corridors open onto trails
    const map = b.map;
    this.fogRt.clear();
    this.fogRt.fill(0x010208, 0.94);
    this.fogRt.erase(this.memoryRt);
    this.liveStampGfx.clear();
    this.linePool.eraseFog(this.liveStampGfx);
    this.fogRt.erase(this.liveStampGfx, -map.xMin, -map.yMin);
    this.liveStampGfx.clear();
    this.pursuerLinePool.eraseFog(this.liveStampGfx);
    this.fogRt.erase(this.liveStampGfx, -map.xMin, -map.yMin);

    // Facing cue only (no player disc)
    this.sonarGfx.clear();
    const fx = snap.player.facingX;
    const fy = snap.player.facingY;
    const fl = Math.hypot(fx, fy) || 1;
    this.sonarGfx.lineStyle(1.5, 0x60a5fa, 0.22);
    this.sonarGfx.lineBetween(
      this.displayX,
      this.displayY,
      this.displayX + (fx / fl) * 18,
      this.displayY + (fy / fl) * 18,
    );

    this.linePool.draw(this.beamGfx);
    this.pursuerLinePool.draw(this.pursuerBeamGfx);

    this.exitGfx.setAlpha(0);

    this.pursuerCue.clear();
    for (let i = 0; i < this.state.pursuers.length; i++) {
      const p = this.state.pursuers[i]!;
      const gfx = this.pursuerGfx[i];
      if (!gfx) continue;
      gfx.setPosition(p.x, p.y);
      const mode = p.mode;
      if (sawPursuer || mode === "chase" || mode === "catching") {
        const pColor =
          mode === "catching"
            ? 0xef4444
            : mode === "chase"
              ? 0xf97316
              : 0xf87171;
        gfx.setFillStyle(pColor, 0.85);
        gfx.setStrokeStyle(2, 0xfecaca, 0.8);
      } else {
        gfx.setFillStyle(0x334155, moving ? 0.12 : 0);
        gfx.setStrokeStyle(1, 0x64748b, moving ? 0.1 : 0);
      }
      if (
        (mode === "investigate" || mode === "sweep") &&
        p.trailValid
      ) {
        // Red skull + cross lock on predicted lastHeard (visual only).
        drawThreatLockCue(this.pursuerCue, p.lastHeardX, p.lastHeardY, {
          fromX: p.x,
          fromY: p.y,
        });
      } else if (mode === "chase" || mode === "catching") {
        this.pursuerCue.lineStyle(2, 0xf97316, 0.7);
        this.pursuerCue.lineBetween(p.x, p.y, this.displayX, this.displayY);
      }
    }

    this.extractBar.clear();
    if (snap.holdProgress > 0 && snap.holdKind) {
      const ringR =
        snap.holdKind === "exit"
          ? b.extractRadius + 6
          : b.lootInteractRadius + 6;
      const ringColor = snap.holdKind === "exit" ? 0x4ade80 : 0xfbbf24;
      this.extractBar.lineStyle(4, ringColor, 0.9);
      this.extractBar.beginPath();
      this.extractBar.arc(
        snap.holdX,
        snap.holdY,
        ringR,
        -Math.PI / 2,
        -Math.PI / 2 + Math.PI * 2 * snap.holdProgress,
        false,
      );
      this.extractBar.strokePath();
    }

    // Loot-open isotropic burst kept as expanding rings (event, not hit mark)
    for (let i = this.bursts.length - 1; i >= 0; i--) {
      const ring = this.bursts[i]!;
      ring.life -= dt;
      const u = Math.max(0, ring.life / ring.maxLife);
      ring.gfx.clear();
      if (ring.life <= 0) {
        ring.gfx.destroy();
        this.bursts.splice(i, 1);
        continue;
      }
      const r = ring.radius * (1.05 - u * 0.2);
      // Radial spokes instead of filled hit discs
      const spokes = 10;
      for (let s = 0; s < spokes; s++) {
        const a = (s / spokes) * Math.PI * 2;
        ring.gfx.lineStyle(2, ring.color, 0.2 + u * 0.55);
        ring.gfx.lineBetween(
          ring.x,
          ring.y,
          ring.x + Math.cos(a) * r,
          ring.y + Math.sin(a) * r,
        );
      }
    }
  }

  private restartRun(): void {
    for (const burst of this.bursts) burst.gfx.destroy();
    this.bursts = [];
    this.linePool.clear();
    this.pursuerLinePool?.clear();
    this.memoryRt?.clear();
    this.memoryPaintRt?.clear();
    this.terrainPaintRt?.clear();
    this.sonarCellKey = "";
    this.sonarCellCoolUntil = 0;
    this.outlineCoolUntil.clear();
    this.sonarClock = 0;
    this.wasMoving = false;
    this.state = restartNoiseExtraction(this.state.seed);
    this.displayX = this.state.player.x;
    this.displayY = this.state.player.y;
    this.buildWorld();
    this.setupCamera();
    this.hud.sync(getHudSnapshot(this.state));
  }

  private quitToTitle(): void {
    if (shouldRestartOnQuit(SceneKey.NoiseExtraction)) {
      this.restartRun();
      return;
    }
    assertTransition(SceneKey.NoiseExtraction, SceneKey.Title);
    this.scene.start(SceneKey.Title);
  }

  private teardown(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.input.off("pointerdown", this.onPointerDown, this);
    this.scale.off("resize", this.onResize, this);
    this.hud?.destroy();
    for (const burst of this.bursts) burst.gfx.destroy();
    this.bursts = [];
  }
}
